import { ReactNode, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useEnvironmentURLs } from '@/atoms'
import { selectUserConfiguration } from '@/features/domain/user'
import { conversionUtils } from '@/server-data'

import Close from '@mui/icons-material/Close'
import { Stack } from '@mui/material'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import { FilePreview, FilePreviewThumbnail } from '@workwave-tidal/lab'

import { form } from '../../../formState'
import * as OrderTexts from '../../../intl'

import type { ExecutionEventsFormValues } from '../../../typings'
import type { OnRemovePOD, Props } from '../typings'

import { preview } from '@/components/form/preview'
import { PodListItemField } from '../fields/PodListItemField'

type PodSignatureProps = {
  onRemove: OnRemovePOD
  signature: uui.domain.client.rm.MediaPod
  index: number
  userConfig: uui.domain.UserConfiguration
} & {
  onTogglePod: Props['onTogglePod']
  activePodMarker: Props['activePodMarker']
}

function PodSignature(props: PodSignatureProps) {
  const { signature, onRemove, index, userConfig } = props
  const { sec, token } = signature

  const { podsBaseUrl } = useEnvironmentURLs()

  const time = conversionUtils.convertTime(userConfig)(sec)
  const title = OrderTexts.getCustomerSignatureLabel()
  const src = `${podsBaseUrl}${token}`

  const actions = [
    <ActionBarItem
      icon={<Close />}
      id="close"
      key="close"
      label={OrderTexts.getCloseButtonLabel()}
      onClick={() => onRemove(signature.uuid)}
    />,
  ]

  return (
    <FilePreview
      actions={actions}
      actionsVisibility="always"
      icon={<FilePreviewThumbnail objectFit="fill" src={src} />}
      subtitle={time}
      title={title}
      tooltip={title}
      variant="standard"
      testId={`pod_signatures_${index}`}
    />
  )
}

export function PodSignatures(props: Props) {
  const { activePodMarker, onTogglePod, values: initialValue } = props
  const userConfig = useSelector(selectUserConfiguration)

  const renderItems = useMemo(
    () =>
      (values: uui.domain.client.rm.Pod[], onRemove: OnRemovePOD): ReactNode => {
        return (
          <Stack spacing={2}>
            {values.length > 0 &&
              values.map((signature, index) => (
                <PodSignature
                  signature={signature as uui.domain.client.rm.MediaPod}
                  index={index}
                  activePodMarker={activePodMarker}
                  onTogglePod={onTogglePod}
                  userConfig={userConfig}
                  onRemove={onRemove}
                  key={index}
                />
              ))}
            {values.length === 0 && <preview.Value>{OrderTexts.getNoneText()}</preview.Value>}
          </Stack>
        )
      },
    [activePodMarker, onTogglePod, userConfig],
  )

  return (
    <>
      {initialValue.formPods.signatures.length > 0 && (
        <Stack spacing={1}>
          <form.SectionSubtitle>{OrderTexts.getSignaturesLabel()}</form.SectionSubtitle>

          <PodListItemField<'formPods.signatures', ExecutionEventsFormValues>
            name="formPods.signatures"
            podListItemFieldInputProps={{
              testId: 'pod-signatures',
              renderItems,
            }}
          />
        </Stack>
      )}
    </>
  )
}
